import React, { useEffect, useRef, useState } from 'react'
import { ContentServiceFlowStatus, RequestError } from './types'
import {
  isRequestError,
  isUnauthorizedResponse,
  isPermanentError,
} from './utility'
import { BorderedContainer } from '../utility/Containers'
import { StatusState } from './StatusState'
import { useStatusPolling } from './hooks'
import { StatusDetails } from './StatusDetails'
import { Box, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'

interface FlowStatusViewProps {
  flowStatus?: ContentServiceFlowStatus
  downloadLink?: React.ReactNode
  onFlowDelete?: () => void
}

export function PollingFlowStatus({
  flowId,
  pollingIntervalMs = 1000,
  onFlowPoll,
  additionalHeaderItems = null,
  onFlowDelete,
}: {
  flowId?: string
  pollingIntervalMs?: number
  onFlowPoll?: (
    flowResults: ContentServiceFlowStatus | RequestError | undefined,
  ) => void
  additionalHeaderItems?: React.ReactNode
  onFlowDelete?: () => void
}) {
  const [flowStatus, setFlowStatus] = useState<
    ContentServiceFlowStatus | RequestError
  >()
  const onFlowPollRef = useRef(onFlowPoll)
  onFlowPollRef.current = onFlowPoll

  useStatusPolling({ flowId, onFlowPoll: setFlowStatus, pollingIntervalMs })

  useEffect(() => {
    onFlowPollRef.current?.(flowStatus)
  }, [flowStatus])

  // No flow_id: render nothing
  if (!flowId) return null

  // Valid flow_id but status not yet loaded: show loading state in the same box
  if (!flowStatus) {
    return (
      <BorderedContainer>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" fontWeight="medium">
            Flow Status
          </Typography>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      </BorderedContainer>
    )
  }

  const permanentError = isPermanentError(flowStatus)

  // Show error message
  const errorMessage = isRequestError(flowStatus)
    ? permanentError
      ? isUnauthorizedResponse(flowStatus)
        ? `Status Error: Unauthorized - ${flowStatus.errorText}`
        : `Status Error: Flow ${flowId} not found`
      : `Status Error: ${flowStatus.errorText}`
    : null

  // If we have a valid flow status, show it (this will clear previous error messages)
  const validFlowStatus = isRequestError(flowStatus) ? undefined : flowStatus

  return (
    <>
      {errorMessage && (
        <Box sx={{ color: 'error.main', mb: 1, fontSize: '0.875rem' }}>
          {errorMessage}
          {!permanentError && ' (Polling will continue...)'}
        </Box>
      )}
      {validFlowStatus && (
        <FlowStatusView
          flowStatus={validFlowStatus}
          downloadLink={additionalHeaderItems}
          onFlowDelete={onFlowDelete}
        />
      )}
    </>
  )
}

function FlowStatusView({
  flowStatus,
  downloadLink,
  onFlowDelete,
}: FlowStatusViewProps) {
  if (!flowStatus?.data) {
    return null
  }

  return (
    <BorderedContainer>
      <StatusState
        flowStatus={flowStatus}
        statusDecorators={downloadLink}
        onFlowDelete={onFlowDelete}
      />
      <StatusDetails flowStatus={flowStatus} />
    </BorderedContainer>
  )
}
